import { groupBy, isEqual, uniqWith } from 'lodash';
import {
  ARTICLE_GROUP_ID,
  ARTICLE_SUBGROUP_ID,
  ARTICLE_SUBSCRIPTION_ID,
} from '@/creditflowresearch/ui/components/SubscriptionForm/const';
import { IUserSubscriptionsResponse } from '@/creditflowresearch/domain/vo/Subscriptions/userSubscriptions';
import { mapArticleTypeToTitle } from '@/creditflowresearch/ui/components/creditFlow/articles/constants';
import { ThemeType } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/themeTypes';
import { AbsProduct } from '@/creditflowresearch/domain/Product';
import { ProfileTypes } from '@/creditflowresearch/application/actions/profile/constants';
import URL from '@/creditflowresearch/Router/urlConstants';
import { ISSUER } from '@/creditflowresearch/application/PageFiltersState/constants';
import { IMarketTheme } from '@/creditflowresearch/domain/vo/ArticlesAndStats/IMarketTheme';
import { IDictionarySector, IDictionaryIndustryMapping } from '@/creditflowresearch/domain/vo/IDictionary';
import type { IRegionFilter } from '@/creditflowresearch/application/PageFilters/hooks/create/useCreateRegionFilters';
import type { IProductFilter } from '@/creditflowresearch/application/PageFilters/hooks/create/useCreateProductFilters';
import Market, { EmergingMarket } from '@/creditflowresearch/domain/Market';
import { getRegionIdsFromMarkets } from '@/creditflowresearch/application/helpers/region';
import { EMEA_REGION_ID } from '@/creditflowresearch/domain/Region/constants';

export enum PageTypes {
  SECTOR = 'sectors',
  INDUSTRY = 'industries',
  COMPANY = 'companies',
  OVERVIEW = 'overview',
}

const getAllSubById = <T>(
  list: T[],
  id: string,
  property: string,
  subProperty: string,
): T[] => list.filter((entity) => entity[property] === id && entity[subProperty]);

type SubEntitiesFilter = {
  [x: string]: string,
  regionId: string,
}[];

export const getSubEntitiesFilter = (
  parent: IDictionarySector | IDictionaryIndustryMapping,
  list: (IDictionarySector | IDictionaryIndustryMapping)[],
  regionId: string,
  parentProperty: string,
  subProperty: string,
  payloadProperty: string,
): SubEntitiesFilter => {
  if (parent?.[subProperty]) {
    return [{ [payloadProperty]: parent[subProperty], regionId }];
  }

  if (parent?.[parentProperty]) {
    return getAllSubById<IDictionarySector | IDictionaryIndustryMapping>(
      list, parent[parentProperty],
      parentProperty,
      subProperty,
    ).map((entity) => ({ [payloadProperty]: entity[subProperty], regionId }));
  }

  return [];
};

const filterThemesByType = (themes: IMarketTheme[], themeType: ThemeType): IMarketTheme[] => {
  const groupedThemesByType = groupBy(themes, 'type');
  return groupedThemesByType[mapArticleTypeToTitle[themeType]] || [];
};

const filterThemesByRegion = (themes: IMarketTheme[], regionIds: string[]): IMarketTheme[] => {
  const regionSet = new Set(regionIds);
  return themes.filter(({ regionId }) => regionSet.has(regionId));
};

const filterThemesByProduct = (themes: IMarketTheme[], productIds: string[]): IMarketTheme[] => {
  const productSet = new Set(productIds);
  return themes.filter(({ productId }) => productSet.has(productId));
};

const filterThemesByAbsSubtype = (themes: IMarketTheme[]): IMarketTheme[] => {
  return themes.filter(({ subtype }) => subtype === AbsProduct.ABBREVIATION);
};

const filterThemesByMarkets = (themes: IMarketTheme[], marketIds: string[]): IMarketTheme[] => {
  const marketsSet = new Set(marketIds);
  return themes.filter(({ marketId }) => marketsSet.has(marketId));
};

const getThemesConsumerOverview = (themes: IMarketTheme[], regionIds: string[]) => {
  const filteredThemesByRegion = filterThemesByRegion(themes, regionIds);
  return filterThemesByAbsSubtype(filteredThemesByRegion);
};

const getEmergingMarketThemes = (themes: IMarketTheme[], marketIds: string[], regionIds: string[]) => {
  const themesByMarkets = filterThemesByMarkets(themes, marketIds);
  return filterThemesByRegion(themesByMarkets, regionIds);
};

export const restrictToSubscribableRegions = (
  regionIds: string[],
  subscribableRegionIds: string[],
): string[] => regionIds.filter((regionId) => subscribableRegionIds.includes(regionId));

// TODO: remove the CEEMEA fallback when the European market opening period ends;
// CEEMEA then locks with the rest and only the add-on gate stays
export const selectSubscribableRegionIds = (
  unlockedRegionIds: string[],
  planRegionIds: string[],
  grantsPlanTopics: boolean,
): string[] => {
  const entitledRegionIds = grantsPlanTopics ? planRegionIds : [];

  if (unlockedRegionIds.every((regionId) => entitledRegionIds.includes(regionId))) {
    return unlockedRegionIds;
  }

  return unlockedRegionIds.filter((regionId) => regionId === EMEA_REGION_ID);
};

export const getThemesByRegionsProducts = ({
  themes,
  regionIds,
  productIds,
  markets,
  isConsumerOverview,
  themeType,
  subscribableRegionIds,
}: {
  themes: IMarketTheme[],
  regionIds: string[],
  productIds: string[],
  markets: string,
  isConsumerOverview: boolean,
  themeType: ThemeType,
  subscribableRegionIds: string[],
}) => {
  let filteredThemesByType = filterThemesByType(themes, themeType);

  if (markets?.includes(EmergingMarket.ABBREVIATION)) {
    const marketIds = Market.getIds(markets);

    return getEmergingMarketThemes(
      filteredThemesByType,
      marketIds,
      restrictToSubscribableRegions(regionIds, subscribableRegionIds),
    );
  }

  if (isConsumerOverview) {
    return getThemesConsumerOverview(
      filteredThemesByType,
      restrictToSubscribableRegions(regionIds, subscribableRegionIds),
    );
  }

  const filteredThemesByRegion = filterThemesByRegion(
    filteredThemesByType,
    restrictToSubscribableRegions(
      markets?.length ? getRegionIdsFromMarkets(markets) : regionIds,
      subscribableRegionIds,
    ),
  );

  return filterThemesByProduct(filteredThemesByRegion, productIds);
};

export const initialArticleParams = {
  countryIds: [],
  sectors: [],
  companies: [],
  industries: [],
  themeIds: [],
};

export const getSubscriptionsArticleParams = (mySubscriptions: IUserSubscriptionsResponse) => {
  const articleParams = mySubscriptions[ARTICLE_GROUP_ID]
      ?.subgroups[ARTICLE_SUBGROUP_ID]
      ?.subscriptions[ARTICLE_SUBSCRIPTION_ID]
      ?.articleParams || initialArticleParams;

  const subscriptionIds: string[] = [];

  Object.values(mySubscriptions).forEach(({ subgroups }) => {
    if (!subgroups) return;

    Object.values(subgroups).forEach(({ subscriptions }) => {
      if (!subscriptions) return;

      Object.values(subscriptions).forEach((sub) => {
        if (sub.isSubscribed) {
          subscriptionIds.push(sub.subscriptionId);
        }
      });
    });
  });

  return { articleParams, subscriptionIds };
};

const profileParamsMap: Record<ProfileTypes, ParamsGenerator> = {
  [ProfileTypes.INDUSTRY]: ({ industry, regionIds, industriesList }) => ({
    industries: getSubEntitiesFilter(
      industry,
      Object.values(industriesList),
      regionIds[0],
      'industryId',
      'subindustryId',
      'subIndustryId',
    ),
  }),
  [ProfileTypes.SECTOR]: ({ sector, regionIds, sectorsList }) => ({
    sectors: getSubEntitiesFilter(
      sector,
      Object.values(sectorsList),
      regionIds[0],
      'sectorId',
      'subsectorId',
      'subSectorId',
    ),
  }),
  [ProfileTypes.OVERVIEW]: ({
    themes, regionIds, productIds, markets, isConsumerOverview, themeType, subscribableRegionIds,
  }) => ({
    themeIds: getThemesByRegionsProducts({
      themes,
      regionIds,
      productIds,
      markets,
      isConsumerOverview,
      themeType,
      subscribableRegionIds,
    }).map(({ id }) => id),
  }),
};

export interface IArticleParamsConfig {
  themes: IMarketTheme[],
  regionIds: string[],
  productIds: string[],
  markets: string,
  segments: string,
  isConsumerOverview: boolean,
  themeType: ThemeType,
  sector: IDictionarySector,
  industry: IDictionaryIndustryMapping,
  sectorsList: Record<string, IDictionarySector>,
  industriesList: Record<string, IDictionaryIndustryMapping>,
  includedParentId: string,
  includedCompanyRole: string,
  pathname: string,
  profileType: ProfileTypes,
  subscribableRegionIds: string[],
}

type ParamsGenerator = (
  config: IArticleParamsConfig,
) => Record<string, SubEntitiesFilter | string[]>;

const getCompanyFilter = ({ includedParentId, includedCompanyRole, pathname }) => {
  if (includedCompanyRole && includedParentId) {
    return [{
      companyId: includedParentId,
      companyType: pathname.includes(URL.ISSUER) ? ISSUER : includedCompanyRole,
    }];
  }
  return [];
};

export const getParams = (config: IArticleParamsConfig) => {
  const paramsGenerator = profileParamsMap[config.profileType];

  return {
    ...initialArticleParams,
    ...(paramsGenerator && paramsGenerator(config)),
    companies: getCompanyFilter(config),
  };
};

export const getPageIsOpened = (query) => {
  const { companyViewId, sector, industry } = query;

  if (companyViewId) return PageTypes.COMPANY;
  if (sector) return PageTypes.SECTOR;
  if (industry) return PageTypes.INDUSTRY;

  return PageTypes.OVERVIEW;
};

const mergeUniqueArrays = (
    arr1: any[],
    arr2: any[] = [],
    arr3: any[] = [],
) => uniqWith([...arr1, ...arr2, ...arr3], isEqual);

export const getSubscribeParams = ({
  subscriptionArticleParams = initialArticleParams,
  paramsConfig,
  pageType,
  articleFormThemes = null,
}) => {
  const {
    sectors,
    themeIds,
    industries,
    companies,
  } = getParams(paramsConfig);
  const articleParams = { ...subscriptionArticleParams };

  const pageTypeParamsMap = {
    [PageTypes.SECTOR]: sectors,
    [PageTypes.INDUSTRY]: industries,
    [PageTypes.COMPANY]: companies,
  };

  if (pageType && articleParams[pageType] && pageTypeParamsMap[pageType]) {
    articleParams[pageType] = mergeUniqueArrays(subscriptionArticleParams[pageType], pageTypeParamsMap[pageType]);
    return articleParams;
  }

  articleParams.themeIds = Array.isArray(articleFormThemes)
      ? articleFormThemes
      : mergeUniqueArrays(subscriptionArticleParams.themeIds, themeIds);

  return articleParams;
};

export const getFilteredThemes = (
  themes: IMarketTheme[],
  currentRegion: IRegionFilter,
  currentProduct: IProductFilter,
) => themes.filter(
  ({ regionId, productId }) => regionId === currentRegion.id && productId === currentProduct?.id,
);
